import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { userAppContext } from '@/context/Context';
import DownloadDump from './DownloadDump';

import ToolsSidebar from '../Tools/ToolsSidebar';

import RInstallation from '../Tools/RPackage/RInstallation';
import RDocumentation from '../Tools/RPackage/RDocumentation';

import CodeExamples from '../Tools/API/CodeExamples';
import InfoandSupport from '../Tools/API/InfoandSupport';
import ApiEndpoints from '../Tools/API/Endpoints';
import InteractiveConsoleDescription from '../Tools/API/InteractiveConsoleDescription';

import Installation from '../Tools/CytoscapeApp/Installation';
import Aida from '../Tools/DisgenetAI/Aida';
import BackgroundPages from '@/global/Background/BackgroundPages';
import EnrichmentTool from '../Tools/Enrichment/Enrichment';
import DSEATool from '../Tools/Enrichment/DSEA';
import MCPTool from '../Tools/MCP/mcpConnector';
import { isDseaEnabled } from '@/modules/enrichment/helpers';

function APIContent() {
  const [searchParams] = useSearchParams();
  const section = searchParams.get('section');

  const { user, isAuthenticated } = userAppContext();
  const userRole = user ? user['cognito:groups'][0] : 'user';
  const rolesAllowedDownloadDump = ['ROLE_UNLIMITED', 'ROLE_DEVELOPER'];

  return (
    <React.Fragment>
      <div className="apply-trial-page mt-20">
        <BackgroundPages />
        <section className="relative z-10 px-4 lg:px-20 md:mt-24 mt-20 home-hero-content-profile">
          <div className="home-hero-section">
            <h1 className="pt-4 mb-6 ml-6">Tools</h1>

            <div className="row txt-contenido pt-2 pb-8">
              <div className="col-12 col-lg-3 order-0 order-md-0 mb-4 mb-lg-0">
                <div className="nav p-0 dropdown-menu-inline dropdown-menu-rounded dropdown-menu-hover-primary">
                  <ToolsSidebar onClick={null} collapsed={false} variant="profile" />
                </div>
              </div>

              <div className="col-12 col-lg-8 order-1 order-lg-1">
                <div className="tab-content md:ml-5" id="toolsTabbedPane">
                  {section === 'Rinstallation' && (
                    <>
                      <h5>Instalation</h5>
                      <ContentCard>
                        <RInstallation />
                      </ContentCard>
                    </>
                  )}
                  {section === 'Rdocumentation' && (
                    <>
                      <h5>Documentation</h5>
                      <ContentCard>
                        <RDocumentation />
                      </ContentCard>
                    </>
                  )}
                  {section === 'console' && (
                    <>
                      <h5>Interactive Console</h5>
                      <ContentCard>
                        <InteractiveConsoleDescription />
                        <Link
                          to="/interactive-console"
                          className="inline-block mt-4 btn-gradient-pink px-6 py-2 rounded-md text-white font-semibold no-underline"
                        >
                          Open Interactive Console
                        </Link>
                      </ContentCard>
                    </>
                  )}
                  {section === 'endpoints' && (
                    <>
                      <h5>Endpoints</h5>
                      <ContentCard>
                        <ApiEndpoints />
                      </ContentCard>
                    </>
                  )}
                  {section === 'examples' && (
                    <>
                      <h5>Code examples</h5>
                      <ContentCard>
                        <CodeExamples />
                      </ContentCard>
                    </>
                  )}
                  {section === 'support' && (
                    <>
                      <h5>Information and support</h5>
                      <ContentCard>
                        <InfoandSupport />
                      </ContentCard>
                    </>
                  )}
                  {section === 'installcyto' && (
                    <>
                      <h5>Installation</h5>
                      <ContentCard>
                        <Installation />
                      </ContentCard>
                    </>
                  )}
                  {section === 'mcp' && (
                    <>
                      <h5>MCP Connector</h5>
                      <ContentCard>
                        <MCPTool />
                      </ContentCard>
                    </>
                  )}
                  {section === 'enrichment' && (
                    <>
                      <h5>Enrichment Analysis</h5>
                      <ContentCard>
                        <EnrichmentTool />
                      </ContentCard>
                    </>
                  )}
                  {isDseaEnabled && section === 'dsea' && (
                    <>
                      <h5>DSEA</h5>
                      <ContentCard>
                        <DSEATool />
                      </ContentCard>
                    </>
                  )}
                  {section === 'download' &&
                    isAuthenticated &&
                    rolesAllowedDownloadDump.includes(userRole) && (
                      <>
                        <h5>DISGENET download</h5>
                        <ContentCard>
                          <p>
                            Only users with a PREMIUM profile can download the files. For information on how
                            to obtain a DISGENET license, contact us at{' '}
                            <a href="mailto:info@disgenet.com">info@disgenet.com</a>. To download DISGENET,
                            please enter your api key.
                          </p>
                          <p>
                            The database is provided as a collection of tsv or{' '}
                            <a href="https://parquet.apache.org/" target="_blank" rel="noopener noreferrer">
                              parquet
                            </a>{' '}
                            files.
                          </p>
                          <DownloadDump />
                          <p className="mt-4">
                            Use this command for downloading the database from the command line:
                          </p>
                          <pre className="dump_code">
                            <code>
                              wget --output-document=disgenet_dump.gz --post-data &apos;api-key=API-KEY&apos;
                              http://api.disgenet.com/dump/download
                            </code>
                          </pre>
                          <p className="mt-4">
                            Use these commands to verify the database was downloaded correctly:
                          </p>
                          <p className="font-semibold mt-2">Windows:</p>
                          <pre className="dump_code">
                            <code>
                              <ol>
                                <li>
                                  1. Download and install a utility that can generate MD5 hashes, such as{' '}
                                  <a href="https://learn.microsoft.com/en-us/windows-server/administration/windows-commands/certutil">
                                    CertUtil
                                  </a>
                                  .
                                </li>
                                <li>2. Open Command Prompt.</li>
                                <li>
                                  3. Navigate to the directory containing the .tar.gz file you want to check.
                                </li>
                                <li>4. Run the following command: CertUtil -hashfile yourfile.tar.gz MD5</li>
                                <li>5. Compare the output with the expected MD5 hash.</li>
                              </ol>
                            </code>
                          </pre>
                          <p className="font-semibold mt-2">macOS and Linux:</p>
                          <pre className="dump_code">
                            <code>
                              <ol>
                                <li>1. Open Terminal.</li>
                                <li>
                                  2. Navigate to the directory containing the .tar.gz file you want to check.
                                </li>
                                <li>
                                  3. Run the following command: md5 yourfile.tar.gz (macOS) or md5sum
                                  yourfile.tar.gz (Linux)
                                </li>
                                <li>4. Compare the output with the expected MD5 hash.</li>
                              </ol>
                            </code>
                          </pre>
                        </ContentCard>
                      </>
                    )}
                  {section === 'assistant' && (
                    <>
                      <h5 className="flex items-center gap-2">
                        AIDA - DISGENET AI assistant
                        <img
                          src="../../static/images/eyesAida.svg"
                          alt="bot-aida"
                          width="30"
                          height="30"
                          className="ml-1"
                        />
                      </h5>
                      <ContentCard>
                        <Aida />
                      </ContentCard>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </React.Fragment>
  );
}

function ContentCard({ children }: { children: React.ReactNode }) {
  return <div className="bg-white border rounded-xl p-6 mb-4">{children}</div>;
}

export default APIContent;
